import React, { useState } from 'react';
import { Player, GameState } from '../../domain/entities';
import { getPlayerValue } from '../../domain/constants';
import { Modal } from '../shared/Modal';

interface SellPlayerModalProps {
    player: Player | null;
    onClose: () => void;
    onConfirm: (priceMultiplier: number) => void;
    isPlayerInLineup: boolean;
    gameState: GameState;
    leagueName: string;
}

export const SellPlayerModal: React.FC<SellPlayerModalProps> = ({ player, onClose, onConfirm, isPlayerInLineup, gameState, leagueName }) => {
    const [priceMultiplier, setPriceMultiplier] = useState(1.0);

    if (!player) return null;

    const baseValue = getPlayerValue(player, leagueName, gameState.gameType, gameState.config);
    const adjustedValue = Math.round(baseValue * priceMultiplier);
    const priceChange = adjustedValue - baseValue;

    return (
        <Modal isOpen={!!player} onClose={onClose}>
            <h2>Confirm Sale</h2>
            {isPlayerInLineup && (
                <p className="negative">Warning: {player.name} is in your starting lineup. Selling them will leave a gap in your team.</p>
            )}
            <p>Are you sure you want to attempt to sell {player.name}?</p>
            <p>Base market value: £{baseValue.toLocaleString()}</p>

            <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                <p style={{ marginBottom: '8px' }}>Asking Price Adjustment:</p>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                        className={`button ${priceMultiplier === 0.9 ? 'secondary' : ''}`}
                        onClick={() => setPriceMultiplier(0.9)}
                        style={{ flex: '1', minWidth: '80px' }}
                    >
                        -10%
                        <br />
                        <span style={{ fontSize: '0.8em', opacity: 0.8 }}>£{Math.round(baseValue * 0.9).toLocaleString()}</span>
                    </button>
                    <button
                        className={`button ${priceMultiplier === 1.0 ? 'secondary' : ''}`}
                        onClick={() => setPriceMultiplier(1.0)}
                        style={{ flex: '1', minWidth: '80px' }}
                    >
                        0%
                        <br />
                        <span style={{ fontSize: '0.8em', opacity: 0.8 }}>£{baseValue.toLocaleString()}</span>
                    </button>
                    <button
                        className={`button ${priceMultiplier === 1.1 ? 'secondary' : ''}`}
                        onClick={() => setPriceMultiplier(1.1)}
                        style={{ flex: '1', minWidth: '80px' }}
                    >
                        +10%
                        <br />
                        <span style={{ fontSize: '0.8em', opacity: 0.8 }}>£{Math.round(baseValue * 1.1).toLocaleString()}</span>
                    </button>
                </div>
            </div>

            <p style={{ textAlign: 'center', fontWeight: 'bold' }}>
                Adjusted asking price: £{adjustedValue.toLocaleString()}
                {priceChange !== 0 && (
                    <span style={{ color: priceChange > 0 ? 'var(--positive-color)' : 'var(--warning-color)', marginLeft: '8px' }}>
                        ({priceChange > 0 ? '+' : ''}{priceChange.toLocaleString()})
                    </span>
                )}
            </p>

            <p style={{ fontSize: '0.9em', opacity: 0.8, marginTop: '12px' }}>
                Lower prices increase chances of a quick sale. Higher prices may take longer to sell or not sell at all.
            </p>

            <div className="modal-actions">
                <button className="button" onClick={onClose}>Cancel</button>
                <button className="button danger" onClick={() => onConfirm(priceMultiplier)}>Confirm</button>
            </div>
        </Modal>
    );
};
